import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Alert, Box, Button, Chip, CircularProgress, LinearProgress, Stack, Tab, Tabs, Typography } from '@mui/material';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdfOutlined';
import TableViewIcon from '@mui/icons-material/TableViewOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import { PageHeader } from '../../components/PageHeader';
import { PdfViewerDialog, PdfViewerSource } from '../../components/pdf/PdfViewerDialog';
import { useAuth } from '../../context/authContext';
import {
  downloadManagementReportExcel,
  fetchManagementReportPdf,
  getManagementOptions,
  getManagementReport,
  ManagementFilters,
  managementReportFilename,
  ManagementReportKey,
} from '../../api/managementReports';
import { isVisibleReport, readFilters, readReport, writeParams } from './filterParams';
import { ManagementFilterBar } from './ManagementFilterBar';
import { KpiCards, NotesCard, ReportCharts, ReportTableCard } from './ReportVisuals';
import { SheetView } from './SheetView';
import { ProgrammeTargetsDialog } from './ProgrammeTargetsDialog';
import { IntakeMappingDialog } from './IntakeMappingDialog';

const TABS: Array<{ key: ManagementReportKey; label: string }> = [
  { key: 'executive', label: 'Executive Overview' },
  { key: 'target-revenue', label: 'Target Revenue' },
  { key: 'programme-performance', label: 'Country, University & Programme' },
  { key: 'intake-performance', label: 'Intake Performance' },
  { key: 'targets', label: 'Target vs Achievement' },
  { key: 'revenue', label: 'Revenue' },
  { key: 'fis', label: 'FIS' },
  { key: 'lead-conversion', label: 'Lead Conversion' },
  { key: 'dropouts', label: 'Dropout Analysis' },
  { key: 'counsellor-performance', label: 'Counsellor Performance' },
  { key: 'university-performance', label: 'University & Intake Progression' },
  { key: 'data-quality', label: 'Data Quality' },
];

/** The "How these figures are counted" card under each report — hidden on
 * screen for now (2026-11-05); the definitions stay in the Excel and PDF
 * exports. Set to true to show it again. */
const SHOW_FIGURE_NOTES = false;

const errorText = (err: unknown) =>
  isAxiosError(err) ? ((err.response?.data as { message?: string } | undefined)?.message ?? 'The report could not be loaded.') : 'The report could not be loaded.';

/** Management Reports — separate from the operational dashboards. Every
 * figure, chart, table and export uses the same filters (kept in the URL),
 * and the API applies the caller's permissions and branch scope. */
export const ManagementReportsPage = () => {
  const [params, setParams] = useSearchParams();
  const report = readReport(params);
  const filters = useMemo(() => readFilters(params), [params]);
  const { hasPermission, user } = useAuth();
  const canExport = hasPermission('management_reports', 'export');
  const canManageTargets = hasPermission('management_reports', 'manage');
  const isSuperAdmin = user?.role?.name === 'Super Admin';
  const [targetsOpen, setTargetsOpen] = useState(false);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [viewer, setViewer] = useState<PdfViewerSource | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const customIncomplete = filters.period === 'custom' && (!filters.from && !filters.to || (!!filters.from && !!filters.to && filters.from > filters.to));
  const options = useQuery({ queryKey: ['management-reports', 'options'], queryFn: getManagementOptions, staleTime: 5 * 60_000 });
  const data = useQuery({
    queryKey: ['management-reports', report, filters],
    queryFn: () => getManagementReport(report, filters),
    enabled: !customIncomplete,
    placeholderData: keepPreviousData,
  });

  const go = (next: ManagementReportKey, nextFilters: ManagementFilters = filters) => setParams(writeParams(next, nextFilters));

  const excel = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await downloadManagementReportExcel(report, filters);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'The report could not be exported.');
    } finally {
      setExporting(false);
    }
  };

  const result = data.data?.key === report ? data.data : undefined;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        icon={InsightsOutlinedIcon}
        color="#5B2DA3"
        title="Management Reports"
        subtitle="Targets, revenue, conversion, counsellor performance, dropouts and departures for management."
        actions={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {(report === 'target-revenue' || isVisibleReport('targets')) && (report === 'target-revenue' || report === 'targets' || report === 'executive' || report === 'programme-performance' || report === 'intake-performance') && (
              <Button variant="outlined" startIcon={<FlagOutlinedIcon />} onClick={() => setTargetsOpen(true)}>
                {canManageTargets ? 'Set targets' : 'Targets'}
              </Button>
            )}
            {report === 'data-quality' && isSuperAdmin && (
              <Button variant="outlined" color="warning" startIcon={<EventRepeatIcon />} onClick={() => setMappingOpen(true)}>
                Intake clean-up
              </Button>
            )}
            {canExport && (
            <>
              <Button variant="outlined" startIcon={exporting ? <CircularProgress size={16} /> : <TableViewIcon />} disabled={!result || exporting} onClick={() => void excel()}>
                Excel
              </Button>
              <Button
                variant="contained"
                startIcon={<PictureAsPdfIcon />}
                disabled={!result}
                onClick={() =>
                  setViewer({
                    title: result?.title ?? 'Management report',
                    filename: managementReportFilename(report, 'pdf'),
                    load: () => fetchManagementReportPdf(report, filters),
                  })
                }
              >
                Preview PDF
              </Button>
            </>
            )}
          </Stack>
        }
      />

      <Tabs value={report} onChange={(_e, value: ManagementReportKey) => go(value)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        {TABS.filter((t) => isVisibleReport(t.key)).map((t) => (
          <Tab key={t.key} value={t.key} label={t.label} sx={{ textTransform: 'none', fontWeight: 600 }} />
        ))}
      </Tabs>

      <ManagementFilterBar report={report} filters={filters} options={options.data} onChange={(next) => go(report, next)} />

      {exportError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError(null)}>
          {exportError}
        </Alert>
      )}
      {customIncomplete && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Choose a valid start and/or end date for the custom period.
        </Alert>
      )}
      {data.isError && <Alert severity="error">{errorText(data.error)}</Alert>}
      {data.isFetching && <LinearProgress sx={{ mb: 1 }} />}
      {!result && data.isLoading && (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      )}

      {result && (
        <Box sx={{ opacity: data.isFetching ? 0.6 : 1, transition: 'opacity 0.2s' }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mr: 1 }}>
              {result.title}
            </Typography>
            <Chip size="small" color="primary" variant="outlined" label={result.periodLabel} />
            {result.previousPeriodLabel && <Chip size="small" variant="outlined" label={`Compared with ${result.previousPeriodLabel}`} />}
            {result.filters.map((f) => (
              <Chip key={f} size="small" variant="outlined" label={f} />
            ))}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: -1, mb: 2 }}>
            {result.periodBasis}
          </Typography>
          {/* A KPI only links to a report that has a tab. */}
          {result.kpis.length > 0 && <KpiCards kpis={result.kpis.map((k) => (k.report && !isVisibleReport(k.report) ? { ...k, report: undefined } : k))} onOpen={(next) => go(next)} />}
          {result.sheet && <SheetView sheet={result.sheet} />}
          {Object.keys(result.charts).length > 0 && <ReportCharts charts={result.charts} />}
          {result.tables.map((t) => (
            <ReportTableCard key={`${result.key}-${t.key}`} table={t} onDrill={(drill) => go(report, { ...filters, ...drill })} />
          ))}
          {SHOW_FIGURE_NOTES && <NotesCard notes={result.notes} />}
        </Box>
      )}

      <PdfViewerDialog source={viewer} onClose={() => setViewer(null)} />
      <ProgrammeTargetsDialog open={targetsOpen} onClose={() => setTargetsOpen(false)} options={options.data} canManage={canManageTargets} />
      {isSuperAdmin && <IntakeMappingDialog open={mappingOpen} onClose={() => setMappingOpen(false)} />}
    </Box>
  );
};
